import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import { useConfig } from '../../../contexts/ConfigContext';

/**
 * ItemSelector Component
 *
 * Allows users to select items from the items catalog.
 * Features:
 * - Search/filter items by name
 * - Sort by name or price
 * - Add items with quantity
 * - Edit quantity and price (price can be adjusted per service)
 * - Line item discounts
 */
interface CatalogItem {
  id: string;
  item_name: string;
  item_code: string | null;
  description: string | null;
  rate: number | null;
  unit: string | null;
  item_type: string | null;
}

export interface SelectedItem {
  id: string;
  catalogItemId: string;
  name: string;
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  discountType: string;
  unit: string;
  sectionLabel?: string | null;
  _priceEditing?: string;
}

interface ItemSelectorProps {
  selectedItems: SelectedItem[];
  onChange: (items: SelectedItem[]) => void;
  disabled?: boolean;
  vatInclusive?: boolean;
}

function ItemSelector({ selectedItems, onChange, disabled = false }: ItemSelectorProps) {
  const { formatCurrency, currencyCode, config } = useConfig();
  const defaultCurrencyCode = currencyCode || config?.currency_code || '';

  const [availableItems, setAvailableItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'price'

  // Load items from items on mount
  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('items')
        .select('id, item_name, item_code, description, rate, unit, item_type')
        .eq('status', 'active')
        .order('item_name');

      if (fetchError) throw fetchError;

      setAvailableItems(data || []);
    } catch (err) {
      console.error('Error loading items:', err);
      setError('Failed to load items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort available items
  const filteredItems = useMemo(() => {
    let items = availableItems;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.item_name.toLowerCase().includes(query) ||
        (item.description && item.description.toLowerCase().includes(query)) ||
        (item.item_code && item.item_code.toLowerCase().includes(query))
      );
    }

    // Sort
    if (sortBy === 'price') {
      items = [...items].sort((a, b) => (a.rate || 0) - (b.rate || 0));
    } else {
      items = [...items].sort((a, b) => a.item_name.localeCompare(b.item_name));
    }

    return items;
  }, [availableItems, searchQuery, sortBy]);

  // Add item to selection
  const handleAddItem = (catalogItem: CatalogItem) => {
    const newItem: SelectedItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      catalogItemId: catalogItem.id,
      name: catalogItem.item_name,
      code: catalogItem.item_code || '',
      description: catalogItem.description || '',
      quantity: 1,
      unitPrice: catalogItem.rate || 0,
      discount: 0,
      discountType: 'percentage',
      unit: catalogItem.unit || 'qty',
    };

    onChange([...selectedItems, newItem]);
    setShowPicker(false);
    setSearchQuery('');
  };

  // Update item in selection (supports single field or object of fields)
  const handleUpdateItem = (index: number, field: string | Record<string, unknown>, value?: string | number | undefined) => {
    const newItems = [...selectedItems];
    if (typeof field === 'object') {
      // Batch update: field is an object of { field: value } pairs
      newItems[index] = { ...newItems[index], ...field } as SelectedItem;
    } else {
      newItems[index] = { ...newItems[index], [field]: value } as SelectedItem;
    }
    onChange(newItems);
  };

  // Remove item from selection
  const handleRemoveItem = (index: number) => {
    onChange(selectedItems.filter((_, i) => i !== index));
  };

  // Move item up/down in the list
  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= selectedItems.length) return;
    const newItems = [...selectedItems];
    const temp = newItems[index]!;
    newItems[index] = newItems[target]!;
    newItems[target] = temp;
    onChange(newItems);
  };

  // Calculate line total for an item (discounts handled at estimate level)
  const calculateLineTotal = (item: SelectedItem) => {
    return item.quantity * item.unitPrice;
  };

  return (
    <div style={styles.container}>
      {/* Selected Items List */}
      {selectedItems.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📦</div>
          <p style={styles.emptyText}>No items added yet</p>
          <p style={styles.emptyHint}>Add items from the catalog below</p>
        </div>
      ) : (
        <div style={styles.itemsList}>
          {selectedItems.map((item, index) => (
            <div key={item.id} style={styles.itemCard}>
              <div style={styles.itemHeader}>
                <div style={styles.itemName}>{item.name}</div>
                {!disabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <button
                      type="button"
                      style={{ ...styles.moveBtn, opacity: index === 0 ? 0.3 : 1 }}
                      onClick={() => handleMoveItem(index, 'up')}
                      disabled={index === 0}
                      aria-label="Move up"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.moveBtn, opacity: index === selectedItems.length - 1 ? 0.3 : 1 }}
                      onClick={() => handleMoveItem(index, 'down')}
                      disabled={index === selectedItems.length - 1}
                      aria-label="Move down"
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      style={styles.removeBtn}
                      onClick={() => handleRemoveItem(index)}
                      aria-label="Remove item"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              <div style={styles.itemControls}>
                {/* Quantity */}
                <div style={styles.qtyControl}>
                  <button
                    type="button"
                    style={styles.qtyBtn}
                    onClick={() => handleUpdateItem(index, 'quantity', Math.max(1, item.quantity - 1))}
                    disabled={disabled}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    style={styles.qtyInput}
                    value={item.quantity}
                    onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                    min={1}
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    style={styles.qtyBtn}
                    onClick={() => handleUpdateItem(index, 'quantity', item.quantity + 1)}
                    disabled={disabled}
                  >
                    +
                  </button>
                </div>

                {/* Unit Price */}
                <div style={styles.priceControl}>
                  <span style={styles.currencyLabel}>{defaultCurrencyCode}</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={styles.priceInput}
                    value={item._priceEditing ?? (item.unitPrice === 0 ? '' : item.unitPrice)}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow empty, digits, and one decimal point
                      if (val === '' || /^\d*\.?\d*$/.test(val)) {
                        // Store raw string for editing, parse for calculations
                        handleUpdateItem(index, {
                          unitPrice: val === '' ? 0 : parseFloat(val) || 0,
                          _priceEditing: val
                        });
                      }
                    }}
                    onBlur={(e) => {
                      // Clean up editing state and ensure valid number
                      const val = parseFloat(e.target.value) || 0;
                      handleUpdateItem(index, {
                        unitPrice: val,
                        _priceEditing: undefined
                      });
                    }}
                    disabled={disabled}
                    placeholder="0.00"
                  />
                </div>

                {/* Line Total */}
                <div style={styles.lineTotal}>
                  {formatCurrency(calculateLineTotal(item))}
                </div>
              </div>

              {/* Per-line-item discounts hidden — discounts are handled at the estimate level
                 (volume discounts, tier discounts, referral/voucher codes) */}
            </div>
          ))}
        </div>
      )}

      {/* Add Item Button / Picker */}
      {!disabled && !showPicker && (
        <button
          type="button"
          style={styles.addBtn}
          onClick={() => setShowPicker(true)}
        >
          + Add Item from Catalog
        </button>
      )}
      {!disabled && showPicker && (
        <div style={styles.picker}>
          <div style={styles.pickerHeader}>
            <input
              type="text"
              style={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items..."
              autoFocus
            />
            <button
              type="button"
              style={styles.closePickerBtn}
              onClick={() => {
                setShowPicker(false);
                setSearchQuery('');
              }}
            >
              ✕
            </button>
          </div>

          <div style={styles.pickerSort}>
            <button
              type="button"
              style={{ ...styles.sortBtn, ...(sortBy === 'name' ? styles.sortBtnActive : {}) }}
              onClick={() => setSortBy('name')}
            >
              A-Z
            </button>
            <button
              type="button"
              style={{ ...styles.sortBtn, ...(sortBy === 'price' ? styles.sortBtnActive : {}) }}
              onClick={() => setSortBy('price')}
            >
              Price
            </button>
          </div>

          <div style={styles.pickerList}>
            {loading ? (
              <div style={styles.pickerLoading}>Loading items...</div>
            ) : error ? (
              <div style={styles.pickerError}>{error}</div>
            ) : filteredItems.length === 0 ? (
              <div style={styles.pickerEmpty}>
                {searchQuery ? 'No items match your search' : 'No items available. Sync items from Zoho first.'}
              </div>
            ) : (
              filteredItems.map(item => (
                <button
                  key={item.id}
                  type="button"
                  style={styles.pickerItem}
                  onClick={() => handleAddItem(item)}
                >
                  <div style={styles.pickerItemName}>{item.item_name}</div>
                  {item.description && (
                    <div style={styles.pickerItemDesc}>{item.description}</div>
                  )}
                  <div style={styles.pickerItemPrice}>{formatCurrency(item.rate || 0)}</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Sync hint if no items */}
      {!loading && availableItems.length === 0 && (
        <div style={styles.syncHint}>
          <p>No items in catalog. Go to <strong>Platform Config</strong> to sync items from Zoho Books.</p>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
  },
  // Empty state
  emptyState: {
    textAlign: 'center',
    padding: '2rem 1rem',
    background: 'var(--color-bg)',
    borderRadius: 'var(--radius)',
    marginBottom: '0.75rem',
  },
  emptyIcon: {
    fontSize: 'var(--text-5xl)',
    marginBottom: '0.5rem',
  },
  emptyText: {
    margin: 0,
    fontWeight: 500,
    color: 'var(--color-text)',
  },
  emptyHint: {
    margin: '0.25rem 0 0 0',
    fontSize: 'var(--text-base)',
    color: 'var(--color-text-light)',
  },
  // Items list
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '0.75rem',
  },
  itemCard: {
    background: 'var(--color-bg)',
    borderRadius: 'var(--radius)',
    padding: '0.75rem',
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
    gap: '0.5rem',
  },
  itemName: {
    fontWeight: 600,
    fontSize: 'var(--text-lg)',
    flex: 1,
  },
  moveBtn: {
    width: '28px',
    height: '28px',
    border: 'none',
    background: 'transparent',
    color: 'var(--color-text-light)',
    cursor: 'pointer',
    fontSize: 'var(--text-2xs)',
    borderRadius: 'var(--radius-sm)',
    flexShrink: 0,
    padding: 0,
  },
  removeBtn: {
    width: '32px',
    height: '32px',
    border: 'none',
    background: 'transparent',
    color: 'var(--color-error)',
    cursor: 'pointer',
    fontSize: 'var(--text-xl)',
    borderRadius: 'var(--radius-md)',
    flexShrink: 0,
  },
  itemControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  qtyControl: {
    display: 'flex',
    alignItems: 'center',
    background: 'white',
    borderRadius: 'var(--radius)',
    border: '2px solid var(--color-border)',
  },
  qtyBtn: {
    width: '40px',
    height: '40px',
    border: 'none',
    background: 'transparent',
    fontSize: 'var(--text-3xl)',
    cursor: 'pointer',
    color: 'var(--color-primary)',
    fontWeight: 600,
  },
  qtyInput: {
    width: '40px',
    border: 'none',
    textAlign: 'center',
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    background: 'transparent',
  },
  priceControl: {
    display: 'flex',
    alignItems: 'center',
    background: 'white',
    borderRadius: 'var(--radius)',
    border: '2px solid var(--color-border)',
    flex: 1,
    minWidth: '100px',
  },
  currencyLabel: {
    padding: '0 0.5rem',
    color: 'var(--color-text-light)',
    fontSize: 'var(--text-base)',
  },
  priceInput: {
    flex: 1,
    border: 'none',
    padding: '0.5rem',
    fontSize: 'var(--text-xl)',
    minHeight: '40px',
    background: 'transparent',
    width: '100%',
  },
  lineTotal: {
    fontWeight: 600,
    fontSize: 'var(--text-xl)',
    color: 'var(--color-primary)',
    minWidth: '70px',
    textAlign: 'right',
  },
  // Discount
  discountDetails: {
    marginTop: '0.5rem',
  },
  discountSummary: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)',
    cursor: 'pointer',
    padding: '0.25rem 0',
  },
  discountDisplay: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)',
    padding: '0.25rem 0',
  },
  discountControls: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  discountInput: {
    flex: 1,
    padding: '0.5rem',
    fontSize: 'var(--text-xl)',
    border: '2px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    minHeight: '40px',
  },
  discountTypeSelect: {
    width: '70px',
    padding: '0.5rem',
    fontSize: 'var(--text-xl)',
    border: '2px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    minHeight: '40px',
  },
  // Add button
  addBtn: {
    width: '100%',
    padding: '0.75rem',
    fontSize: 'var(--text-lg)',
    fontWeight: 600,
    color: 'var(--color-primary)',
    background: 'white',
    border: '2px dashed var(--color-primary)',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    minHeight: '48px',
  },
  // Picker
  picker: {
    background: 'white',
    border: '2px solid var(--color-primary)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
  },
  pickerHeader: {
    display: 'flex',
    padding: '0.75rem',
    gap: '0.5rem',
    borderBottom: '1px solid var(--color-border)',
  },
  searchInput: {
    flex: 1,
    padding: '0.75rem',
    fontSize: 'var(--text-xl)',
    border: '2px solid var(--color-border)',
    borderRadius: 'var(--radius)',
    minHeight: '44px',
  },
  closePickerBtn: {
    width: '44px',
    height: '44px',
    border: '2px solid var(--color-border)',
    borderRadius: 'var(--radius)',
    background: 'white',
    cursor: 'pointer',
    fontSize: 'var(--text-3xl)',
    color: 'var(--color-text-light)',
  },
  pickerSort: {
    display: 'flex',
    padding: '0.5rem 0.75rem',
    gap: '0.5rem',
    borderBottom: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
  },
  sortBtn: {
    padding: '0.5rem 1rem',
    fontSize: 'var(--text-md)',
    fontWeight: 500,
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    background: 'transparent',
    color: 'var(--color-text-light)',
  },
  sortBtnActive: {
    background: 'white',
    color: 'var(--color-primary)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  pickerList: {
    maxHeight: '300px',
    overflowY: 'auto',
  },
  pickerLoading: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--color-text-light)',
  },
  pickerError: {
    padding: '1rem',
    textAlign: 'center',
    color: 'var(--color-error)',
    background: 'var(--color-error-bg)',
  },
  pickerEmpty: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--color-text-light)',
  },
  pickerItem: {
    width: '100%',
    padding: '0.875rem 1rem',
    border: 'none',
    borderBottom: '1px solid var(--color-border)',
    background: 'white',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  pickerItemName: {
    fontWeight: 500,
    fontSize: 'var(--text-lg)',
  },
  pickerItemDesc: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  pickerItemPrice: {
    fontSize: 'var(--text-base)',
    fontWeight: 600,
    color: 'var(--color-primary)',
  },
  // Sync hint
  syncHint: {
    marginTop: '1rem',
    padding: '1rem',
    background: 'var(--color-warning-bg)',
    borderRadius: 'var(--radius)',
    fontSize: 'var(--text-base)',
    color: 'var(--color-warning-text)',
    textAlign: 'center',
  },
};

export default ItemSelector;
